"use client";

import { useMemo } from "react";
import Link from "next/link";
import { ArrowUp, ExternalLink } from "lucide-react";
import { ActionCard } from "@/components/ActionCard";
import {
  MonitoringTrendChart,
  type MonitoringTrendPoint,
} from "@/components/cards/MonitoringTrendChart";
import { AHREFS_DOMAIN_RATING_ATTRIBUTION_URL } from "@/lib/domain-rating-constants";

export function DomainRatingCard() {
  const data = useMemo<MonitoringTrendPoint[]>(() => {
    // Illustrative DR climb (demo data, not real Ahrefs values).
    const drValues = [28, 28, 29, 31, 32, 32, 34, 35, 37, 38, 40, 41, 42];
    const today = new Date();
    return drValues.map((dr, idx) => {
      const date = new Date(today);
      date.setDate(date.getDate() - (drValues.length - 1 - idx));
      return { date: date.toISOString().slice(0, 10), overall: null, dr };
    });
  }, []);

  const latest = data[data.length - 1]?.dr ?? 0;
  const first = data[0]?.dr ?? 0;
  const delta = latest - first;

  const header = (
    <div className="mb-4 w-full aspect-[16/9] rounded-lg border border-border/60 bg-muted/10 p-3 flex flex-col">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-full"
            aria-hidden
            style={{ backgroundColor: "#8b5cf6" }}
          />
          <span className="text-[11px] text-muted-foreground/80">
            Domain Rating
          </span>
        </div>
        <div className="inline-flex items-baseline gap-1.5">
          <span className="text-sm font-semibold text-foreground">
            DR {latest}
          </span>
          {delta > 0 ? (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-500">
              <ArrowUp className="h-2.5 w-2.5" aria-hidden />
              {delta}
            </span>
          ) : null}
        </div>
      </div>
      <div className="mt-2 flex-1">
        <MonitoringTrendChart data={data} metric="dr" />
      </div>
    </div>
  );

  const footer = (
    <Link
      href={AHREFS_DOMAIN_RATING_ATTRIBUTION_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-3 inline-flex w-fit items-center gap-1 text-[11px] font-medium text-muted-foreground/80 transition-colors hover:text-foreground"
    >
      <span>Domain Rating by Ahrefs</span>
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </Link>
  );

  return (
    <ActionCard
      ariaLabel="Ahrefs Domain Rating"
      title="Ahrefs Domain Rating"
      subtitle="Track your official Ahrefs Domain Rating over time and get an instant email the moment it moves."
      header={header}
      footer={footer}
    />
  );
}
